import {
  draw,
  ground,
  PASI_HEIGHT,
  PASI_WIDTH,
  resize,
  SURFACE_WIDTH,
  TREES_WIDTH,
  width,
  SCALE,
  loadSprites,
  getSprites,
} from './renderer'
import { audioCtx, playBoom, playJump, playScore, playNewRecord } from './audio'

const GRAVITY = 2220 * SCALE // px/s^2
const FIRST_SPAWN_DELAY = 0.4
export const TNT_SPAWN_INTERVAL_MIN = 1.2
export const SPAWN_INTERVAL_MIN = 0.95
export const SPAWN_INTERVAL_MAX = 1.35
const OBSTACLE_SPEED = 340 * SCALE // px/s
const TREE_SPEED = OBSTACLE_SPEED * 0.8
const JUMP_IMPULSE = -625 * SCALE // px/s
const JUMP_LIMIT = 2
export const MAX_DT = 0.1

export const pasi = {
  x: 50 * SCALE,
  y: 0,
  width: PASI_WIDTH,
  height: PASI_HEIGHT,
  vy: 0,
}
export let pasiFlipped = false
export let pasiFlash = false

const clearFlash = () => {
  pasiFlash = false
}
let jumpsLeft = JUMP_LIMIT
let last = performance.now()
let spawnTimer = FIRST_SPAWN_DELAY
let score = 0
let groundOffset = 0
let treesOffset = 0
let frameId: number | undefined

const HIGH_SCORE_KEY = 'pasihyppelyHighScore'
const LAST_SCORE_KEY = 'pasihyppelyLastScore'

export const getHighScore = (): number => {
  const saved = localStorage.getItem(HIGH_SCORE_KEY)
  return saved ? parseInt(saved, 10) : 0
}

const saveHighScore = (value: number) => {
  localStorage.setItem(HIGH_SCORE_KEY, value.toString())
}

export const getLastScore = (): number => {
  const saved = localStorage.getItem(LAST_SCORE_KEY)
  return saved ? parseInt(saved, 10) : 0
}

const saveLastScore = (value: number) => {
  localStorage.setItem(LAST_SCORE_KEY, value.toString())
}

export const cancel = () => {
  if (frameId) {
    cancelAnimationFrame(frameId)
  }
}

export type ObstacleType = 'crates' | 'spikes' | 'tnt'

interface Obstacle {
  x: number
  y: number
  width: number
  height: number
  type: ObstacleType
  passed?: boolean
}

export const obstacles: Obstacle[] = []

interface ScorePopup {
  x: number
  y: number
  age: number
}

export const SCORE_POPUP_DURATION = 0.6
const SCORE_POPUP_SPEED = 40 * SCALE

export const scorePopups: ScorePopup[] = []

export const addScorePopup = (x: number, y: number) => {
  scorePopups.push({ x, y, age: 0 })
}

export const updateScorePopups = (dt: number) => {
  for (const p of scorePopups) {
    p.age += dt
    p.y -= SCORE_POPUP_SPEED * dt
  }
  for (let i = scorePopups.length - 1; i >= 0; i--) {
    if (scorePopups[i].age > SCORE_POPUP_DURATION) {
      scorePopups.splice(i, 1)
    }
  }
}

export let newRecordAchieved = false
export const clearNewRecord = () => {
  newRecordAchieved = false
}
export const setNewRecord = () => {
  newRecordAchieved = true
}

export const startGame = (
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  gameStateRef: React.RefObject<'menu' | 'playing' | 'gameover'>,
  setGameState: React.Dispatch<React.SetStateAction<'menu' | 'playing' | 'gameover'>>,
  setScore: React.Dispatch<React.SetStateAction<number>>
) => {
  reset(canvasRef.current)
  gameStateRef.current = 'playing'
  last = performance.now() // Reset last for dt calculation

  loadSprites()
    .then(() => {
      frameId = requestAnimationFrame(
        loop.bind(null, gameStateRef, canvasRef, setGameState, setScore)
      )
    })
    .catch((error) => {
      console.error('Error loading images:', error)
    })
}

export const createObstacleNodes = (): Record<
  ObstacleType,
  { img: HTMLImageElement; width: number; height: number }
> => {
  const { cratesImg, spikesImg, tntImg } = getSprites()

  return {
    crates: { img: cratesImg, width: 73 * SCALE, height: 56 * SCALE },
    spikes: { img: spikesImg, width: 119 * SCALE, height: 16 * SCALE },
    tnt: { img: tntImg, width: 36 * SCALE, height: 84 * SCALE },
  }
}

export const clampDt = (dt: number): number => Math.min(dt, MAX_DT)

export const getSpawnDelay = (type: ObstacleType): number => {
  const min = type === 'tnt' ? TNT_SPAWN_INTERVAL_MIN : SPAWN_INTERVAL_MIN
  return min + Math.random() * (SPAWN_INTERVAL_MAX - min)
}

const loop = (
  gameStateRef: React.RefObject<'menu' | 'playing' | 'gameover'>,
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  setGameState: React.Dispatch<React.SetStateAction<'menu' | 'playing' | 'gameover'>>,
  setScore: React.Dispatch<React.SetStateAction<number>>,
  time: number
) => {
  const dt = clampDt((time - last) / 1000)
  last = time
  update(gameStateRef, dt, setGameState, setScore)
  draw(canvasRef, treesOffset, groundOffset)
  frameId = requestAnimationFrame(loop.bind(null, gameStateRef, canvasRef, setGameState, setScore))
}

const update = (
  gameStateRef: React.RefObject<'menu' | 'playing' | 'gameover'>,
  dt: number,
  setGameState: React.Dispatch<React.SetStateAction<'menu' | 'playing' | 'gameover'>>,
  setScore: React.Dispatch<React.SetStateAction<number>>
) => {
  if (gameStateRef.current !== 'playing') {
    return
  }

  spawnTimer -= dt
  if (spawnTimer <= 0) {
    const types: ObstacleType[] = ['crates', 'spikes', 'tnt']
    const type = types[Math.floor(Math.random() * types.length)]
    const sprite = createObstacleNodes()[type]
    const newObstacle = {
      x: width,
      y: ground - sprite.height,
      width: sprite.width,
      height: sprite.height,
      type,
    }
    obstacles.push(newObstacle)
    spawnTimer = getSpawnDelay(type)
  }

  obstacles.forEach((o) => {
    o.x -= OBSTACLE_SPEED * dt
  })
  if (obstacles.length && obstacles[0].x + obstacles[0].width < 0) {
    obstacles.shift()
  }

  groundOffset = (groundOffset - OBSTACLE_SPEED * dt) % SURFACE_WIDTH
  treesOffset = (treesOffset - TREE_SPEED * dt) % TREES_WIDTH

  pasi.vy += GRAVITY * dt
  pasi.y += pasi.vy * dt
  if (pasi.y > ground - pasi.height) {
    pasi.y = ground - pasi.height
    pasi.vy = 0
    jumpsLeft = JUMP_LIMIT
  }

  for (const o of obstacles) {
    if (!o.passed && pasi.x > o.x + o.width) {
      o.passed = true
      score++
      setScore(score)
      playScore()
      addScorePopup(pasi.x + PASI_WIDTH / 2, pasi.y)
    }
    if (collision(o)) {
      playBoom()
      const best = getHighScore()
      if (score > best) {
        saveHighScore(score)
        newRecordAchieved = true
        playNewRecord()
      }
      saveLastScore(score)
      pasiFlipped = true
      pasiFlash = true
      setTimeout(clearFlash, 300)
      gameStateRef.current = 'gameover'
      setGameState('gameover')
      break
    }
  }

  updateScorePopups(dt)
}

const reset = (canvas: HTMLCanvasElement | null) => {
  if (!canvas) return
  resize(canvas)

  pasi.x = 50 * SCALE
  pasi.vy = 0
  obstacles.length = 0
  scorePopups.length = 0
  spawnTimer = FIRST_SPAWN_DELAY
  score = 0
  jumpsLeft = JUMP_LIMIT
  groundOffset = 0 // Reset ground offset
  treesOffset = 0 // Reset trees offset
  pasiFlipped = false
  pasiFlash = false
  newRecordAchieved = false
}

const collision = (o: Obstacle) => {
  return !(
    pasi.x + pasi.width < o.x ||
    pasi.x > o.x + o.width ||
    pasi.y + pasi.height < o.y ||
    pasi.y > o.y + o.height
  )
}

export const handleJump = (gameStateRef: React.RefObject<'menu' | 'playing' | 'gameover'>) => {
  if (gameStateRef.current !== 'playing') {
    return
  }
  if (audioCtx.state === 'suspended') {
    void audioCtx.resume()
  }
  if (jumpsLeft > 0) {
    pasi.vy = JUMP_IMPULSE
    jumpsLeft--
    playJump()
  }
}

export const handleVisibilityChange = (
  visibility: DocumentVisibilityState,
  gameStateRef: React.RefObject<'menu' | 'playing' | 'gameover'>,
  setGameState: React.Dispatch<React.SetStateAction<'menu' | 'playing' | 'gameover'>>
) => {
  if (visibility === 'hidden' && gameStateRef.current === 'playing') {
    cancel()
    gameStateRef.current = 'menu'
    setGameState('menu')
  }
}
